import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AuthShell } from "@/pages/auth/Login";

export default function Signup() {
  const navigate = useNavigate();
  const [ref, setRef] = useState({ districts: [], campuses: [], teams: [] });
  const [allowParent, setAllowParent] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "",
    district_id: "", campus_id: "", team_id: "",
    phone: "", district_admin_name: "", district_admin_email: "",
    campus_admin_name: "", campus_admin_email: "",
  });
  const [role, setRole] = useState("team_member");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/reference").then(({ data }) => {
      setRef(data);
      setAllowParent(!!data.parent_account_enabled);
      if (data.districts.length === 1) setForm((f) => ({ ...f, district_id: data.districts[0].id }));
    }).catch(() => toast.error("Failed to load districts"));
  }, []);

  const campuses = useMemo(
    () => ref.campuses.filter((c) => c.district_id === form.district_id),
    [ref.campuses, form.district_id]
  );
  const teams = useMemo(
    () => ref.teams.filter((t) => t.campus_id === form.campus_id),
    [ref.teams, form.campus_id]
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const now = new Date();
  const minGradYear = (now.getMonth() >= 5 ? now.getFullYear() : now.getFullYear() - 1) + 1;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.district_id || !form.campus_id || !form.team_id) {
      toast.error("Please select district, campus and team");
      return;
    }
    setLoading(true);
    try {
      if (role === "parent") {
        const { first_name, last_name, email, password, district_id, campus_id, team_id } = form;
        const { data } = await api.post("/auth/signup-parent", { first_name, last_name, email, password, district_id, campus_id, team_id });
        toast.success(data.message || "Parent request submitted!", { duration: 8000 });
        navigate("/pending");
        return;
      }
      if (role === "coach") {
        const { first_name, last_name, email, password, district_id, campus_id, team_id,
                phone, district_admin_name, district_admin_email, campus_admin_name, campus_admin_email } = form;
        const { data } = await api.post("/auth/signup-coach", {
          first_name, last_name, email, password, district_id, campus_id, team_id,
          phone: phone || null,
          district_admin_name: district_admin_name || null, district_admin_email: district_admin_email || null,
          campus_admin_name: campus_admin_name || null, campus_admin_email: campus_admin_email || null,
        });
        toast.success(data.message || "Coach signup submitted! Awaiting approval.", { duration: 8000 });
        navigate("/pending");
        return;
      }
      if (!form.graduation_year) {
        toast.error("Please enter your graduation year");
        setLoading(false);
        return;
      }
      if (Number(form.graduation_year) < minGradYear) {
        toast.error(`Graduation year must be ${minGradYear} or later.`);
        setLoading(false);
        return;
      }
      await api.post("/auth/signup", { ...form, graduation_year: Number(form.graduation_year) });
      toast.success("Signup submitted! Awaiting approval.");
      navigate("/pending");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Create account" subtitle="Sign up for RallyPointGo. Your account needs approval before you can log in.">
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <div className="space-y-2">
          <Label>I am a</Label>
          <div className={`grid gap-2 ${allowParent ? "grid-cols-3" : "grid-cols-2"}`} data-testid="signup-role-toggle">
            <button type="button" onClick={() => setRole("team_member")} data-testid="signup-role-member"
              className={`rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors ${role === "team_member" ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
              Team Member
            </button>
            {allowParent && (
              <button type="button" onClick={() => setRole("parent")} data-testid="signup-role-parent"
                className={`rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors ${role === "parent" ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                Parent / Guardian
              </button>
            )}
            <button type="button" onClick={() => setRole("coach")} data-testid="signup-role-coach"
              className={`rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors ${role === "coach" ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
              Coach
            </button>
          </div>
          {role === "parent" && (
            <p className="text-xs text-slate-400" data-testid="signup-parent-hint">
              We'll match your email to your student's emergency contact and send a request to their coach. Pick your student's team below.
            </p>
          )}
          {role === "coach" && (
            <p className="text-xs text-slate-400" data-testid="signup-coach-hint">
              Pick your campus and team. If your team already has a coach, they'll approve your request; otherwise the System Administrator will. Optionally list your administrators to send them an invite.
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>First name</Label>
            <Input required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="signup-firstname-input" />
          </div>
          <div className="space-y-2">
            <Label>Last name</Label>
            <Input required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="signup-lastname-input" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="signup-email-input" />
        </div>
        <div className="space-y-2">
          <Label>Password</Label>
          <Input type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="signup-password-input" />
        </div>
        <div className="space-y-2">
          <Label>District</Label>
          <Select value={form.district_id} onValueChange={(v) => setForm((f) => ({ ...f, district_id: v, campus_id: "", team_id: "" }))}>
            <SelectTrigger data-testid="signup-district-select"><SelectValue placeholder="Select district" /></SelectTrigger>
            <SelectContent>
              {ref.districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Campus</Label>
            <Select value={form.campus_id} onValueChange={(v) => setForm((f) => ({ ...f, campus_id: v, team_id: "" }))} disabled={!form.district_id}>
              <SelectTrigger data-testid="signup-campus-select"><SelectValue placeholder="Campus" /></SelectTrigger>
              <SelectContent>
                {campuses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Team</Label>
            <Select value={form.team_id} onValueChange={(v) => set("team_id", v)} disabled={!form.campus_id}>
              <SelectTrigger data-testid="signup-team-select"><SelectValue placeholder="Team" /></SelectTrigger>
              <SelectContent>
                {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {role === "team_member" && (
          <div className="space-y-2">
            <Label>Graduation Year</Label>
            <Input type="number" required min={minGradYear} max={2100} value={form.graduation_year}
              onChange={(e) => set("graduation_year", e.target.value)}
              placeholder="e.g. 2028" data-testid="signup-gradyear-input" />
            <p className="text-xs text-slate-400">Enter your graduation year — e.g. 2028 if you graduate in Spring 2028. Your account deactivates the following Fall.</p>
          </div>
        )}
        {role === "coach" && (
          <div className="space-y-3 rounded-lg border border-slate-100 bg-slate-50 p-3">
            <div className="space-y-2">
              <Label>Phone <span className="text-slate-400">(optional)</span></Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="signup-coach-phone" />
            </div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Your administrators (optional)</p>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="District Admin name" value={form.district_admin_name} onChange={(e) => set("district_admin_name", e.target.value)} data-testid="signup-da-name" />
              <Input type="email" placeholder="District Admin email" value={form.district_admin_email} onChange={(e) => set("district_admin_email", e.target.value)} data-testid="signup-da-email" />
              <Input placeholder="Campus Admin name" value={form.campus_admin_name} onChange={(e) => set("campus_admin_name", e.target.value)} data-testid="signup-ca-name" />
              <Input type="email" placeholder="Campus Admin email" value={form.campus_admin_email} onChange={(e) => set("campus_admin_email", e.target.value)} data-testid="signup-ca-email" />
            </div>
            <p className="text-xs text-slate-400">We'll email an invite link to any administrator you list — creating their account from that link grants instant access.</p>
          </div>
        )}
        <Button type="submit" disabled={loading} data-testid="signup-submit-button" className="w-full rounded-full bg-slate-900 hover:bg-slate-800">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create account
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">
        Already have an account?{" "}
        <Link to="/login" className="font-semibold text-sky-600 hover:underline" data-testid="goto-login-link">Sign in</Link>
      </p>
    </AuthShell>
  );
}
